import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ConfirmModal, TextField } from '@nexds/web'

import { BaseUnit, Unit } from '@/domain/protocols/Unit'

import { baseUnitsMapper, UnitField } from '../../AircraftScreen/components/UnitField'
import { Backdrop, InputsContainer, ModalContainer } from './ModalPresenter.styles'

import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useI18n } from '@/presentation/hooks/useI18n'
import { withNumericLimit } from '@/presentation/utils/numericFieldLimit'
import { drawerModalSubject, globalModalSubject, ModalLocale, ModalUtil } from '@/utils/modalUtil'
import { BASE_UNIT_NUMERIC_LIMITS } from '@/utils/numericFieldLimits'
import { isNotSet, isSet } from '@/utils/validation'

interface ModalPresenterProps {
  locale: ModalLocale
}

function ModalPresenter(props: ModalPresenterProps) {
  const { locale } = props

  const { t } = useI18n()

  const modalList = useBehaviorSubject(locale === 'drawer' ? drawerModalSubject : globalModalSubject)
  const currentModal = modalList[0]

  const hideModal = ModalUtil[locale].hide

  const defaultPositiveLabel = useMemo(() => t('MODAL_BUTTON_DEFAULT_CONFIRM'), [])

  const getInputUnits = () =>
    currentModal?.inputs?.map((input) => {
      return input.currentUnit ?? baseUnitsMapper[input.unit][0]
    }) ?? []

  const getInputValues = () => currentModal?.inputs?.map((input) => (isSet(input.value) ? input.value : '')) ?? []

  const [inputValues, setInputValues] = useState<string[]>([])
  const [inputUnits, setInputUnits] = useState<Unit[]>([])
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({})

  // O ConfirmModal do @nexds não expõe `disabled` nos botões e o modal só fecha depois que a promise
  // do handler resolve, então cliques repetidos disparavam onPositive/onNegative em paralelo (duas
  // aeronaves criadas + POSTs concorrentes com o mesmo id se atropelando no backend). Ref em vez de
  // state porque a guarda precisa valer já no mesmo tick do segundo clique.
  const isHandlingActionRef = useRef(false)

  const runExclusively = useCallback(async (action: () => Promise<void>) => {
    if (isHandlingActionRef.current) return
    isHandlingActionRef.current = true
    try {
      await action()
    } finally {
      isHandlingActionRef.current = false
    }
  }, [])

  useEffect(() => {
    if (currentModal?.inputs) {
      const isThereInputWithValue = currentModal.inputs.some((input) => isSet(input.value))
      if (isThereInputWithValue) {
        setInputValues(getInputValues())
      }
      const isThereInputWithUnit = currentModal.inputs.some((input) => isSet(input.currentUnit))
      if (isThereInputWithUnit) {
        setInputUnits(getInputUnits())
      }
    }
  }, [currentModal?.inputs])

  const handleInputChange = useCallback(
    (index: number, value: string) => {
      const newValues = [...inputValues]
      newValues[index] = value
      setInputValues(newValues)
      setInputErrors((previousErrors) => {
        const inputId = currentModal.inputs?.[index]?.id
        if (!inputId || !previousErrors[inputId]) return previousErrors

        const nextErrors = { ...previousErrors }
        delete nextErrors[inputId]
        return nextErrors
      })
      currentModal.inputs?.[index].onChange?.(value)
    },
    [inputValues, currentModal]
  )

  const handleUnitChange = useCallback(
    (index: number, unit: Unit) => {
      const newUnits = [...inputUnits]
      newUnits[index] = unit
      setInputUnits(newUnits)
      currentModal.inputs?.[index].onChangeUnit?.(unit)
    },
    [inputUnits, currentModal]
  )

  const handleClose = useCallback(() => {
    hideModal(currentModal?.id)
    if (currentModal?.onClose) currentModal.onClose()
    setInputValues([])
    setInputUnits([])
    setInputErrors({})
  }, [currentModal, inputUnits, inputValues])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      handleClose()
    }

    document.getElementById('map-container')?.addEventListener('click', handleClickOutside)

    return () => {
      document.getElementById('map-container')?.removeEventListener('click', handleClickOutside)
    }
  }, [currentModal?.id, handleClose])

  if (modalList.length === 0) return null
  return (
    <ModalContainer id="ModalContainer">
      <Backdrop onClick={handleClose} />

      <ConfirmModal
        isOpen={modalList.length > 0}
        title={currentModal.title}
        hasDivider={currentModal.hasDivider}
        description={currentModal.headerDescription}
        hasDescription={!!currentModal.headerDescription}
        positiveLabel={currentModal.positiveLabel ?? defaultPositiveLabel}
        negativeLabel={currentModal.negativeLabel}
        positiveColor={currentModal.positiveColor}
        buttonsSize="xs"
        maxWidth={currentModal.maxWidth}
        maxHeight={currentModal.maxHeight}
        hasCloseButton={currentModal.hasCloseButton}
        onPositive={() =>
          runExclusively(async () => {
            const mappedValues = currentModal.inputs?.map((input, index) => ({
              id: input.id,
              value: inputValues[index] ?? null,
              unit: inputUnits[index] ?? input.currentUnit
            }))

            const nextInputErrors =
              currentModal.inputs?.reduce<Record<string, string>>((errors, input, index) => {
                const error = input.validate?.(inputValues[index] ?? '')
                if (error) errors[input.id] = error
                return errors
              }, {}) ?? {}

            if (Object.keys(nextInputErrors).length > 0) {
              setInputErrors(nextInputErrors)
              return
            }

            if (currentModal.onPositive) {
              const shouldClose = await currentModal.onPositive(mappedValues)
              if (isSet(shouldClose) && !shouldClose) {
                return
              }
              hideModal(currentModal.id)
            }
            setInputValues([])
            setInputUnits([])
            setInputErrors({})
          })
        }
        onNegative={() =>
          runExclusively(async () => {
            const shouldClose = await currentModal.onNegative?.()
            if (isSet(shouldClose) && !shouldClose) {
              return
            }
            hideModal(currentModal.id)
            setInputValues([])
            setInputUnits([])
            setInputErrors({})
          })
        }
        onClose={handleClose}
      >
        {currentModal?.inputs && (
          <InputsContainer>
            {currentModal?.inputs?.map((input, index) =>
              input.unit ? (
                <UnitField
                  key={input.id}
                  baseUnit={input.unit}
                  value={isSet(inputValues[index]) ? inputValues[index] : null}
                  currentUnit={inputUnits[index]}
                  label={input.label}
                  onChangeUnit={(unit) => {
                    handleUnitChange(index, unit as Unit)
                  }}
                  {...withNumericLimit(
                    { onChange: (e) => handleInputChange(index, e.target.value) },
                    { max: BASE_UNIT_NUMERIC_LIMITS[input.unit] }
                  )}
                />
              ) : (
                <TextField
                  key={input.id}
                  label={input.label}
                  placeholder={input.placeholder}
                  value={
                    input.valueToUpperCase ? (inputValues[index]?.toUpperCase() ?? '') : (inputValues[index] ?? '')
                  }
                  size="sm"
                  error={!!inputErrors[input.id]}
                  helpMessage={inputErrors[input.id]}
                  helpGutter={!!inputErrors[input.id]}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                />
              )
            )}
          </InputsContainer>
        )}
        {currentModal.content}
      </ConfirmModal>
    </ModalContainer>
  )
}

export { ModalPresenter }
